import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';

const WARNING_AFTER_MS = 10 * 60 * 1000; // show warning after 10 min idle
const LOGOUT_AFTER_MS = 15 * 60 * 1000;  // auto logout after 15 min idle
const COUNTDOWN_SECONDS = 5 * 60;         // 5 minute countdown shown in the modal

// Wraps protected pages. Watches for user activity (mouse, keyboard, scroll,
// touch). After 10 minutes of no activity, shows a warning modal with a
// 5-minute countdown. If the user doesn't respond, they're signed out and
// redirected to /login. Any activity while the modal is NOT showing resets
// the idle timer.
function IdleTimeout({ children }) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const navigate = useNavigate();

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const clearAllTimers = () => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  };

  const handleLogout = useCallback(async () => {
    clearAllTimers();
    try {
      await signOut();
    } catch {
      // Ignore — user is being logged out regardless.
    }
    navigate('/login', { replace: true });
  }, [navigate]);

  const startWarningCountdown = useCallback(() => {
    setShowWarning(true);
    setSecondsLeft(COUNTDOWN_SECONDS);

    countdownIntervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    logoutTimerRef.current = setTimeout(() => {
      handleLogout();
    }, LOGOUT_AFTER_MS - WARNING_AFTER_MS);
  }, [handleLogout]);

  const resetTimers = useCallback(() => {
    // Don't reset activity timers while the warning modal is showing —
    // only the explicit "Stay Logged In" button should dismiss it.
    if (showWarning) return;

    clearAllTimers();
    warningTimerRef.current = setTimeout(startWarningCountdown, WARNING_AFTER_MS);
  }, [showWarning, startWarningCountdown]);

  const handleStayLoggedIn = () => {
    setShowWarning(false);
    clearAllTimers();
    warningTimerRef.current = setTimeout(startWarningCountdown, WARNING_AFTER_MS);
  };

  useEffect(() => {
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

    activityEvents.forEach((event) => {
      window.addEventListener(event, resetTimers);
    });

    // Start the initial timer on mount.
    warningTimerRef.current = setTimeout(startWarningCountdown, WARNING_AFTER_MS);

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetTimers);
      });
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {children}
      {showWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            width: '400px',
            textAlign: 'center'
          }}>
            <h2 style={{ color: '#1a365d', marginBottom: '12px' }}>
              Are you still there?
            </h2>
            <p style={{ color: '#666', marginBottom: '8px' }}>
              You've been inactive for a while. For security, you'll be logged out automatically in:
            </p>
            <p style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#e53e3e',
              margin: '16px 0'
            }}>
              {formatTime(secondsLeft)}
            </p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                onClick={handleLogout}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: 'white',
                  color: '#1a365d',
                  border: '1px solid #1a365d',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Log Out Now
              </button>
              <button
                onClick={handleStayLoggedIn}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#1a365d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default IdleTimeout;